import { Controller } from "@hotwired/stimulus";

/**
 * Copies a row's value, the way PlayerDetailView's contact rows do.
 *
 * A pasteboard write has no visible result on its own, so the app dims the value to secondary for
 * a moment and lets it fade back; that fade is the only confirmation the row gives. The same here,
 * through a `data-copied` attribute the stylesheet reacts to, so the timing lives in one place and
 * the appearance lives in the other.
 *
 * `navigator.clipboard` is tried first and is allowed to fail: it needs a secure context, which
 * http://<lan-ip>:8080 is not, and even in one it rejects with NotAllowedError when the document
 * is not focused. So the result is caught rather than awaited bare - an uncaught rejection there
 * skips the feedback and leaves the row looking inert - and `document.execCommand` through a
 * throwaway textarea is the fallback for both cases.
 */
export default class extends Controller<HTMLElement> {
  static override targets = ["value"];

  static override values = {
    /* What the target says while the copy is still fresh, for a row whose value is a code rather
       than a word: dimming a twelve-character reference and letting it fade back is not visibly
       different from nothing happening at all. Left unset, the row keeps the fade on its own. */
    copiedText: String,
    resetAfter: { type: Number, default: 1500 },
    text: String,
  };

  declare readonly copiedTextValue: string;
  declare readonly resetAfterValue: number;
  declare readonly textValue: string;
  declare readonly valueTarget: HTMLElement;

  private restore: string | undefined;

  private timer: ReturnType<typeof setTimeout> | undefined;

  override disconnect(): void {
    this.clearTimer();
  }

  async copy(event: Event): Promise<void> {
    event.preventDefault();

    const text = this.textValue || (this.valueTarget.textContent ?? "").trim();
    if (text === "") {
      return;
    }

    if (!(await this.write(text))) {
      return;
    }

    this.element.setAttribute("data-copied", "true");

    if (this.copiedTextValue !== "") {
      /* Captured on the first copy only: a second tap before the timer runs out would otherwise
         record the confirmation as the thing to restore. */
      this.restore ??= this.valueTarget.textContent ?? "";
      this.valueTarget.textContent = this.copiedTextValue;
    }

    this.clearTimer();
    this.timer = setTimeout(() => this.reset(), this.resetAfterValue);
  }

  private reset(): void {
    this.element.removeAttribute("data-copied");

    if (this.restore !== undefined) {
      this.valueTarget.textContent = this.restore;
      this.restore = undefined;
    }
  }

  private async write(text: string): Promise<boolean> {
    if (window.isSecureContext && navigator.clipboard !== undefined) {
      try {
        await navigator.clipboard.writeText(text);

        return true;
      } catch {
        /* Falls through to the textarea below. */
      }
    }

    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";

    document.body.appendChild(textarea);
    textarea.select();

    try {
      return document.execCommand("copy");
    } finally {
      document.body.removeChild(textarea);
    }
  }

  private clearTimer(): void {
    if (this.timer !== undefined) {
      clearTimeout(this.timer);
      this.timer = undefined;
    }
  }
}
